/**
 * ./src/services/dashboard.js
 *
 * File that contains axios middleware and functions.
 * Axios is a promise-based HTTP Client for node.js that allow us to communicate with the app's back end.
 *
 */

import { sendRequest } from "./sendRequest";

/*
 *  OPTIONS
 */

/**
 *
 * @returns Object with year data
 */
export const getYearOptions = () => sendRequest(`/years`);

/**
 *
 * @param {string} year Year from 2017 to current
 * @returns Month data for the requested year
 */
export const getMonthOptions = (year) => sendRequest(`/months/${year}`);

/**
 *
 * @param {string} year Year from 2017 to current
 * @param {string} lang Language "es" or "en"
 * @returns Quarter options for the requested year
 */
export const getQuarterOptions = (year, lang) =>
  sendRequest(`/quarters/${year}/${lang}`);

/**
 *
 * @returns Options for years and months
 */
export const getYearMonthOptions = () => sendRequest(`/years-months`);

/**
 *
 * @param {string} onlyMainTopics Whether all topics and subtopics are requested or only main topics ("true" or "false")
 * @param {string} language Language ("es" or "en")
 * @returns Data object
 */
export const getTopicOptions = (onlyMainTopics, language) => {
  if (language === undefined) language = "es";
  return sendRequest(`/topics/${onlyMainTopics}/${language}`);
};

/**
 * Tweets Volume
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current Year
 * @returns Data object
 */
export const getTweetsVolumeData = (countryCode, year) =>
  sendRequest(`/tweets-volume/${countryCode}/${year}`);

/**
 * News Coverage
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current Year
 * @returns Data object
 */
export const getNewsCoverageData = (countryCode, year) =>
  sendRequest(`/news-coverage/${countryCode}/${year}`);

/**
 * Principal Topics
 *
 * @param {string} countryCode Country code
 * @param {string} year  Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @param {string} isXeno Whether data is only for xenophobic or all tweets ("true" or "false")
 * @returns Data object
 */
export const getPrincipalTopicsData = (countryCode, year, month, isXeno) =>
  sendRequest(`/principal-topics/${countryCode}/${year}/${month}/${isXeno}`);

/**
 * Thematic evolution
 *
 * @param {string} countryCode Country code
 * @param {string} topic Topic
 * @param {string} year  Year from 2017 to current
 * @returns Data object
 */
export const getThematicEvolutionData = (countryCode, topic, year) =>
  sendRequest(`/thematic-evolution/${countryCode}/${topic}/${year}`);

/**
 * City Activity (Ratio)
 *
 * @param {string} countryCode County code
 * @param {string} year  Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @param {string} isXeno Whether data is only for xenophobic or all tweets ("true" or "false")
 * @returns Data object
 */
export const getCityActivityRatio = (countryCode, year, month, isXeno) =>
  sendRequest(`/city-activity-ratio/${countryCode}/${year}/${month}/${isXeno}`);

/**
 * City Activity (Volume)
 *
 * @param {string} countryCode County code
 * @param {string} year  Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @param {string} isXeno Whether data is only for xenophobic or all tweets ("true" or "false")
 * @returns Data object
 */
export const getCityActivityVolume = (countryCode, year, month, isXeno) =>
  sendRequest(
    `/city-activity-volume/${countryCode}/${year}/${month}/${isXeno}`
  );

/*
 * Migratory Sentiment
 */

/**
 * Migratory Sentiment
 *
 * @param {string} countryCode County code
 * @param {string} year  Year from 2017 to current
 *
 * @returns Data object
 */
export const getMigratorySentimentData = (countryCode, year) =>
  sendRequest(`/migratory-sentiment/${countryCode}/${year}`);

/**
 * Migratory Sentiment by topic
 *
 * @param {string} countryCode County code
 * @param {string} topic Topic
 * @param {string} year  Year from 2017 to current
 *
 * @returns Data object
 */
export const getSentimentByTopicData = (countryCode, topic, year) =>
  sendRequest(`/mig-sentiment-topic/${countryCode}/${topic}/${year}`);

// Migratory Sentiment Map

/**
 * Migratory Sentiment Map
 *
 * @param {string} year  Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @param {string} sentiment Sentiment ("positive" or "neutral")
 * @returns Data object
 */
export const getMigSentimentMapData = (year, month, sentiment) =>
  sendRequest(`/mig-sentiment-map/${year}/${month}/${sentiment}`);

/**
 * Institutional Accounts Reactions
 *
 * @param {string} countryCode Country code
 * @param {string} year  Year from 2017 to current
 * @returns Data object
 */
export const getInstAccountsReactionsData = (countryCode, year) =>
  sendRequest(`/inst-accounts-reactions/${countryCode}/${year}`);

/*
 * Xenophobia
 */

/**
 * Xenophobic Prevalence
 *
 * @param {string} countryCode  Country code
 * @param {string} year  Year from 2017 to current
 * @returns Data object
 */
export const getXenoPrevalenceData = (countryCode, year) =>
  sendRequest(`/xeno-prevalence/${countryCode}/${year}`);

/**
 * Xenophobic Prevalence Map
 *
 * @param {string} year  Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns Data object
 */
export const getXenoPrevalenceMapData = (year, month) =>
  sendRequest(`/xeno-prevalence-map/${year}/${month}`);

//
/**
 * Xenophobic Accounts
 *
 * @param {string} countryCode Country code
 * @param {string} year  Year from 2017 to current
 * @returns Data object
 */
export const getXenoAccountsData = (countryCode, year) =>
  sendRequest(`/xeno-accounts/${countryCode}/${year}`);

/**
 * Tweet Reach
 *
 * @param {string} countryCode  Country code
 * @param {string} year  Year from 2017 to current
 * @returns Data object
 */
export const getTweetReachData = (countryCode, year) =>
  sendRequest(`/tweet-reach/${countryCode}/${year}`);

/*
 * Perception Surveys
 */

/**
 *
 * @param {string} countryCode Country code
 * @param {string} waveCode Wave code
 * @param {string} questionCode Question code
 * @param {string} variableCode Variable code
 * @returns Data object
 */
export const getPerceptionSurveyData = (
  countryCode,
  waveCode,
  questionCode,
  variableCode
) =>
  sendRequest(
    `/perception-surveys/${countryCode}/${waveCode}/${questionCode}/${variableCode}`
  );
